import React, { FC } from 'react'

import {
  BasicDoughnut,
  DoughnutContent,
  SplitContent,
  SplitContentProps,
  theme,
} from '@acorns/web-components'

import { Portfolio } from 'generated/types'
import Grid from 'pages/invest/Grid'
import { useCrypto } from 'pages/portfolio/Crypto'
import {
  PORTFOLIO_DESCRIPTION,
  PORTFOLIO_ETF,
  PORTFOLIO_VALUE,
} from 'utils/testing/testIDs'

import { usePortfolioContent } from '../hooks/usePortfolioContent'
import { MinimalRelativeContainer, StyledTabs } from '../styled-components'
import { RootProps } from '../types'
import ConsiderationsContent from './ChangeRiskComponents/ConsiderationsContent'
import { LoadingContainer } from './LoadingContainer'

export type PreviewContentProps = RootProps & {
  portfolio: Portfolio
  product?: string
  hideCryptoPercentages?: boolean
}

type EtfPercentageType = {
  bondEtfPercent: string
  stockEtfPercent: string
  cryptoEtfPercent: string
}

const getDoughnutSplitContent = (
  currentPortfolioIncludesCrypto: boolean,
  EtfPercentage: EtfPercentageType,
): SplitContentProps => {
  if (currentPortfolioIncludesCrypto) {
    return {
      topContent: [
        {
          label: 'Crypto',
          value: EtfPercentage.cryptoEtfPercent || '0%',
          bold: true,
          color: theme.colors.purple,
        },
      ],
      bottomContent: [
        {
          label: 'Stocks',
          value: EtfPercentage.stockEtfPercent,
          bold: true,
          color: theme.colors.greenDark,
        },

        {
          label: 'Bonds',
          value: EtfPercentage.bondEtfPercent,
          bold: true,
        },
      ],
    }
  } else {
    return {
      topContent: [
        {
          label: 'Bonds',
          value: EtfPercentage.bondEtfPercent,
        },
      ],
      bottomContent: [
        {
          label: 'Stocks',
          value: EtfPercentage.stockEtfPercent,
          color: theme.colors.greenDark,
        },
      ],
    }
  }
}

export const PreviewContent: FC<PreviewContentProps> = ({
  header,
  portfolio,
  portfolios,
  onETFPress,
  graphData,
  selectedView,
  product,
  hideCryptoPercentages = false,
}) => {
  const {
    selectedDataIndex,
    handleSelectedDataChange,
    chartData,
    chartArcColors,
    totalBondsPercentage: bondEtfPercent,
    totalCryptoPercentage: cryptoEtfPercent,
    totalStocksPercentage: stockEtfPercent,
  } = usePortfolioContent({ portfolio, graphData, selectedView, product })
  const { currentPortfolioIncludesCrypto } = useCrypto()

  const { topContent, bottomContent } = getDoughnutSplitContent(
    currentPortfolioIncludesCrypto && !hideCryptoPercentages,
    {
      bondEtfPercent,
      cryptoEtfPercent: cryptoEtfPercent ?? '0%',
      stockEtfPercent,
    },
  )

  const PREVIEW_PORTFOLIO_OPTIONS = [
    {
      label: 'Overview',
      content: (
        <Grid
          testIdPrefix={PORTFOLIO_ETF}
          items={portfolio?.portfolioSecurityAllocations || []}
          onItemPress={onETFPress}
          selectedIndex={selectedDataIndex}
          mobilePadding={0}
          hasPercentage={true}
        />
      ),
    },
    {
      label: 'Considerations',
      content: (
        <ConsiderationsContent risk={portfolio?.riskLevel?.toLowerCase()} />
      ),
    },
  ]

  return (
    <LoadingContainer portfolios={portfolios}>
      {header}
      <MinimalRelativeContainer>
        <BasicDoughnut
          testId={PORTFOLIO_DESCRIPTION}
          data={chartData}
          colors={chartArcColors}
          onItemHighlight={handleSelectedDataChange}
          options={{
            animation: {
              animateRotate: true,
              animateScale: false,
            },
            round: true,
          }}
        >
          <DoughnutContent
            testId={PORTFOLIO_VALUE}
            defaultContent={
              <SplitContent
                topContent={topContent}
                bottomContent={bottomContent}
              />
            }
          />
        </BasicDoughnut>
      </MinimalRelativeContainer>
      <StyledTabs
        options={PREVIEW_PORTFOLIO_OPTIONS}
        isDescriptionDisplayed={true}
      />
    </LoadingContainer>
  )
}
